import type { ICellRendererParams } from 'ag-grid-community';
import { ResourceRow } from './ResourceRow';
import type { NormalizedRow } from '../types/resource';
import { useResourceSelectionStore } from '../stores/resourceSelectionStore';
import { useResourceViewerStore } from '../stores/resourceViewerStore';

// ag-Grid (v22) React cell renderer for the single-column list view. Renders the media
// row via the shared ResourceRow component instead of an Angular-$compile'd HTML string.
// showCollection/selectable come from the column's cellRendererParams.
interface Params extends ICellRendererParams {
  showCollection?: boolean;
  selectable?: boolean;
}

export function ResourceListCellRenderer(params: Params) {
  const row = params.data as NormalizedRow | undefined;
  // Subscribe narrowly to this row's selection state so a toggle re-renders only
  // the affected checkbox (zustand compares the derived boolean with Object.is).
  const selected = useResourceSelectionStore((s) => (row ? s.isSelected(row.id) : false));
  // In select-all mode rows are locked selected (no per-row exclusion).
  const selectDisabled = useResourceSelectionStore((s) => s.mode === 'all');
  const toggleRow = useResourceSelectionStore((s) => s.toggleRow);
  // Highlight the row that is the document currently open in the viewer, so the
  // highlight follows prev/next navigation. Match the full identity (project +
  // collection + resource) since a browse set can span collections.
  const active = useResourceViewerStore(
    (s) =>
      !!row &&
      s.isOpen &&
      s.config?.resourceId === row.id &&
      s.config?.collectionId === row.collectionId &&
      s.config?.projectId === row.projectId
  );
  // Infinite-model rows are undefined until their block loads. Render an empty element
  // (not null) so ag-grid-react's waitForInstance sees a created component — a null render
  // leaves it logging "React Component ... not created within 1000ms".
  if (!row) return <span aria-hidden="true" />;
  return (
    <ResourceRow
      resource={row}
      showCollection={params.showCollection}
      selectable={params.selectable}
      selected={selected}
      selectDisabled={selectDisabled}
      onToggleSelect={(r) => toggleRow(r.id)}
      active={active}
    />
  );
}
